import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading.tsx";
import SSHCredentialsForm from "@via/pages/account/profile/credentials/credential-key-form/ssh/ssh-credentials-form.tsx";
import {
  cancelGeneratedSSHKey,
  fetchGeneratedSSHKeys,
  useCreateCredential,
} from "@via/pages/account/profile/profile.api.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { USER_ROLES } from "@via/utils/constants.tsx";
import { removeEmptyStrings } from "@via/utils/helpers.tsx";
import { CredentialKeys } from "@via/utils/interfaces.tsx";
import {
  GeneratePendingSshKeyResponse,
  Ssh,
  UpsertCredentialsRequestBody,
} from "common";
import { JSX, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

export default function EditSshCredentialForm({
  credential,
  setOpen,
}: {
  credential: Ssh;
  setOpen: (open: boolean) => void;
}): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;
  const [data, setData] = useState<GeneratePendingSshKeyResponse | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CredentialKeys>({
    mode: "onChange",
    // An unrendered field (the paste box until Replace) is neither validated nor submitted.
    shouldUnregister: true,
    defaultValues: {
      ssh: {
        id: credential.id,
        name: credential.name ?? "",
        hide: Boolean(credential.hide),
        publicKey: credential.publicKey?.trim() ?? "",
      },
    },
  });

  const {
    mutateAsync: updateCredential,
    data: updateCredentialResponse,
    isPending: isUpdateCredentialLoading,
    isSuccess: isUpdateCredentialSuccess,
  } = useCreateCredential();

  const handleClickEvent = () => {
    fetchGeneratedSSHKeys().then(response => {
      setData(response);
    });
  };

  const handleClose = () => {
    // Discard a generated key that was not saved. It also expires on the server.
    if (data?.pendingKeyId && !isUpdateCredentialSuccess) {
      void cancelGeneratedSSHKey(data.pendingKeyId);
    }
    setOpen(false);
  };

  const handleSaveCredential = async (payload: CredentialKeys) => {
    if (payload.ssh) {
      payload.ssh.id = credential.id;
      // The API returns hide as 0/1 from MySQL; coerce to boolean for Joi validation.
      // Only an administrator sees the selector, so everyone else keeps the stored value.
      payload.ssh.hide = isAdmin
        ? Boolean(payload.ssh.hide)
        : Boolean(credential.hide);
    }
    await updateCredential(
      removeEmptyStrings(payload) as UpsertCredentialsRequestBody
    );
  };

  useEffect(() => {
    if (isUpdateCredentialSuccess && updateCredentialResponse) {
      handleClose();
    }
  }, [isUpdateCredentialSuccess, updateCredentialResponse]);

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleSaveCredential)(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              Edit SSH Key
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={handleClose}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Edit the fields below and click Save to update
            </p>
          </div>
        </div>

        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <SSHCredentialsForm
                mode="edit"
                hasPrivateKey={Boolean(credential.hasPrivateKey)}
                managedByAdmin={Boolean(credential.hide) && !isAdmin}
                role={user?.userRole}
                register={register}
                errors={errors}
                setValue={setValue}
                data={data}
                onClick={handleClickEvent}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="edit-ssh-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="edit-ssh-save"
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isUpdateCredentialLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
}
